import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useAuth } from "zudoku/hooks";
import { useTenantStore } from "../stores/tenantStore";

function isLogoutInteraction(value: string | null) {
  if (!value) {
    return false;
  }

  if (value === "logout") {
    return true;
  }

  if (value.includes("signout") || value.includes("logout")) {
    return true;
  }

  try {
    const parsed = JSON.parse(value) as { type?: string };
    return parsed.type === "signout" || parsed.type === "logout";
  } catch {
    return false;
  }
}

export default function usePersistentLogin() {
  const { providerData, isAuthenticated } = useAuth();
  const { instance } = useMsal();
  const [tokenLastRefreshed, setTokenLastRefreshed] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const prevAuthenticated = useRef(isAuthenticated);
  const clearTenant = useTenantStore((state) => state.clearTenant);

  // Handle redirect-based logout: Zudoku's signOut calls logoutRedirect() then setLoggedOut(),
  // but logoutRedirect() navigates the page so setLoggedOut() never runs.
  // Zudoku persists auth state to localStorage ("auth-state"), which survives the redirect.
  // We clear it in beforeunload once MSAL has written a logout/signout marker.
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isLogoutInteraction(sessionStorage.getItem("msal.interaction.status"))) {
        localStorage.removeItem("auth-state");
        clearTenant();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [clearTenant]);

  // Handle non-redirect logout: when setLoggedOut() is called directly (second click scenario
  // where MSAL has no accounts so logoutRedirect is skipped), clear our MSAL cache and tenant.
  useEffect(() => {
    if (prevAuthenticated.current && !isAuthenticated) {
      instance.clearCache();
      clearTenant();
    }
    prevAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, instance, clearTenant]);

  useEffect(() => {
    const trySilentLogin = async () => {
      if (!isAuthenticated || !providerData) return;

      setIsRefreshing(true);

      try {
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          const response = await instance.acquireTokenSilent({
            scopes: ["openid", "profile", "email", "offline_access"],
            account: accounts[0],
          });

          if (response.accessToken) {
            setTokenLastRefreshed(Date.now());
          }
        } else {
          // Fallback: rely on Zudoku token
          setTokenLastRefreshed(Date.now());
        }
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          return;
        }
      } finally {
        setIsRefreshing(false);
      }
    };

    trySilentLogin();
  }, [instance, isAuthenticated, providerData]);

  return { tokenLastRefreshed, isRefreshing };
}
